import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Linkedin, Mic, GitBranch, Plus, GripVertical, Wand2, Save, Play } from "lucide-react";
import { toast } from "sonner";

const iconFor = (ch) => (ch === "email" ? Mail : ch === "linkedin" ? Linkedin : Mic);

function StepCard({ step, index, onDragStart, onDragOver, onDrop, onGenerate, onPreviewVoice, generating, activeIdx }) {
  const Icon = iconFor(step.channel);
  const isVoiceStep = step.type === "voice";
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, index)}
      data-testid={`sequence-step-${index}`}
      className="rounded-xl border border-white/5 bg-slate-950/40 p-4 hover:border-sky-400/30 transition"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-slate-600 cursor-grab" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Day {step.day}</span>
        </div>
        <Icon className="w-4 h-4 text-sky-300" />
      </div>
      <div className="mt-3 text-sm font-semibold capitalize">{step.channel}{isVoiceStep && " · voice"}</div>
      <div className="mt-0.5 text-xs text-slate-400">{step.label}</div>
      {step.variants?.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {step.variants.map((v, i) => (
            <div key={v.label || i} className="rounded-md border border-white/5 bg-white/[0.02] p-2 text-[11px] leading-snug text-slate-300">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[9px] font-mono text-sky-300 uppercase tracking-widest">Variant {v.label}</div>
                {isVoiceStep && (
                  <button onClick={(e) => { e.stopPropagation(); onPreviewVoice(v.text); }} data-testid={`sequence-voice-preview-${index}-${i}`} className="text-[9px] font-mono text-emerald-300 hover:text-emerald-200 flex items-center gap-1">
                    <Play className="w-2.5 h-2.5" /> Play
                  </button>
                )}
              </div>
              {v.text}
            </div>
          ))}
        </div>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={() => onGenerate(index)}
        disabled={generating && activeIdx === index}
        data-testid={`sequence-variant-${index}`}
        className="mt-3 w-full h-8 text-xs border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
      >
        <Wand2 className="w-3 h-3 mr-1" />
        {generating && activeIdx === index ? "Generating…" : "Generate AI variant"}
      </Button>
    </div>
  );
}

export default function Sequences() {
  const [tpl, setTpl] = useState({ steps: [] });
  const [agents, setAgents] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [editing, setEditing] = useState(null);
  const [steps, setSteps] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [dragIdx, setDragIdx] = useState(-1);

  const loadAll = () =>
    Promise.all([
      api.get("/sequences/template/plg-7-touch"),
      api.get("/agents"),
      api.get("/sequences"),
    ]).then(([t, a, s]) => {
      setTpl(t.data);
      setAgents(a.data);
      setSequences(s.data);
      if (s.data.length && !editing) {
        setEditing(s.data[0]);
        setSteps(s.data[0].steps);
      }
    });

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const deployTemplate = async () => {
    if (!agents.length) return toast.error("Deploy an AI agent first");
    const { data } = await api.post("/sequences", {
      name: "PLG 7-Touch Playbook",
      agent_id: agents[0].agent_id,
      steps: tpl.steps.map((s) => ({ ...s, variants: [] })),
    });
    setEditing(data);
    setSteps(data.steps);
    toast.success("Sequence deployed");
    loadAll();
  };

  const onDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx < 0 || dragIdx === idx) return;
    const next = [...steps];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setSteps(next);
    setDragIdx(-1);
  };

  const generateVariant = async (idx) => {
    setGenerating(true);
    setActiveIdx(idx);
    try {
      const s = steps[idx];
      const nextLabel = String.fromCharCode(65 + (s.variants?.length || 0));
      const agent = agents[0] || {};
      const { data } = await api.post("/ai/step-variant", {
        step: s,
        agent_persona: agent.persona || "Consultative",
        tone: agent.tone || "Friendly",
        value_prop: agent.value_prop || "AI BDRs that book meetings on autopilot",
        variant: nextLabel,
      });
      const next = [...steps];
      next[idx] = { ...s, variants: [...(s.variants || []), { label: data.variant, text: data.text }] };
      setSteps(next);
    } catch {
      toast.error("Could not generate variant");
    } finally {
      setGenerating(false);
      setActiveIdx(-1);
    }
  };

  const saveEdits = async () => {
    if (!editing) return;
    const { data } = await api.patch(`/sequences/${editing.sequence_id}`, { steps });
    setEditing(data);
    setSteps(data.steps);
    toast.success("Sequence saved");
    loadAll();
  };

  const activate = async () => {
    if (!editing) return;
    const { data } = await api.patch(`/sequences/${editing.sequence_id}`, { status: "active" });
    setEditing(data);
    toast.success("Sequence activated — AI BDR is live");
    loadAll();
  };

  const previewVoice = async (text) => {
    try {
      const { data } = await api.post("/ai/voice-note", { text, voice: "nova", model: "tts-1" });
      new Audio(`${BACKEND_URL}${data.url}`).play();
      toast.success("Playing AI voice");
    } catch {
      toast.error("Voice preview failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="sequences-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">7-touch sequence builder</h1>
          <p className="mt-1 text-sm text-slate-400">Drag to reorder. Generate AI copy variants for A/B testing.</p>
        </div>
        <div className="flex gap-2">
          {editing && (
            <>
              <Button variant="outline" onClick={saveEdits} data-testid="sequence-save-btn" className="border-white/10 bg-white/[0.02]"><Save className="w-4 h-4 mr-1" /> Save</Button>
              <Button onClick={activate} data-testid="sequence-activate-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full">Activate</Button>
            </>
          )}
          <Button onClick={deployTemplate} data-testid="sequences-create-btn" className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Deploy template
          </Button>
        </div>
      </div>

      {editing && (
        <>
          <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GitBranch className="w-4 h-4 text-sky-300" />
              <div>
                <div className="font-semibold">{editing.name}</div>
                <div className="text-xs text-slate-500">{steps.length} steps · agent {editing.agent_id}</div>
              </div>
            </div>
            <Badge className={editing.status === "active" ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/20" : "bg-slate-500/15 text-slate-300 border-white/10"}>● {editing.status}</Badge>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            {steps.map((s, i) => (
              <StepCard key={i} step={s} index={i} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} onGenerate={generateVariant} onPreviewVoice={previewVoice} generating={generating} activeIdx={activeIdx} />
            ))}
          </div>
        </>
      )}

      {sequences.length > 1 && (
        <div>
          <div className="text-sm font-semibold mb-3">All sequences</div>
          <div className="space-y-2">
            {sequences.map((s) => (
              <button
                key={s.sequence_id}
                onClick={() => { setEditing(s); setSteps(s.steps); }}
                data-testid={`sequence-row-${s.sequence_id}`}
                className={`w-full text-left rounded-xl border ${editing?.sequence_id === s.sequence_id ? "border-sky-400/40 bg-sky-500/5" : "border-white/5 bg-white/[0.02]"} p-4 flex items-center justify-between hover:bg-white/[0.04] transition`}
              >
                <div>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-slate-500">{s.steps.length} steps</div>
                </div>
                <Badge className={s.status === "active" ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/20" : "bg-slate-500/15 text-slate-300 border-white/10"}>● {s.status}</Badge>
              </button>
            ))}
          </div>
        </div>
      )}

      {!editing && (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-slate-500" data-testid="sequences-empty">
          No sequences yet — deploy the template above to activate.
        </div>
      )}
    </div>
  );
}
